import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError
} from "axios";
import { auth } from "../firebase";
import { getAuthToken, waitForAuth } from "../utils/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:7071/api";

export type CheckoutRequest = {
  checkoutType: "plan" | "extra_pages";
  planCode?: string;
  extraPages?: 100 | 500 | 1000;
  successUrl: string;
  cancelUrl: string;
};

export type StripeOrderItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
};

export type StripeOrderResponse = {
  paymentConfirmed: true;
  transaction_id: string;
  value: number;
  currency: string;
  checkout_type: "plan" | "extra_pages" | null;
  plan_code: string | null;
  extra_pages: number | null;
  items: StripeOrderItem[];
};


// FIX: Add ChangePlanRequest type
export type ChangePlanRequest = {
  planCode: string;
  prorationDate?: number;
};

// FIX: Add ChangePlanResponse type
export type ChangePlanResponse = {
  success: boolean;
  subscriptionId: string;
  requestedPlanCode: string;
  status: string;
  paymentPending: boolean;
  actionRequired?: boolean;
  paymentMethodRequired?: boolean;
  hostedInvoiceUrl?: string | null;
  invoiceId?: string | null;
  invoiceStatus?: string | null;
  paymentIntentStatus?: string | null;
  autoRenew?: boolean;
  cancelAtPeriodEnd?: boolean;
  message: string;
};

export type PlanChangePreviewResponse = {
  currentPlanCode: string;
  targetPlanCode: string;
  amountDue: number;
  currency: string;
  newPlanProratedCharge: number;
  currentPlanCredit: number;
  taxAmount: number;
  nextRecurringAmount: number;
  billingInterval: "month" | "year" | null;
  nextRenewalDate: string | null;
  paymentMethod: { brand: string; last4: string } | null;
  pageLimit: number;
  prorationDate: number;
  autoRenew: boolean;
  cancelAtPeriodEnd: boolean;
};

export type SubscriptionResponse = {
  hasSubscription?: boolean;
  plan: number | string | null;
  basePlanCode?: string;
  basePlanName?: string;
  basePlanPages?: number;
  period: number | null;
  monthlyLimit: number;
  extraPages?: number;
  totalAvailable?: number;
  pagesUsed: number;
  pagesReserved?: number;
  pagesRemaining: number;
  status: string | number | null;
  periodKey?: string | null;
  // NEW anniversary fields
  subscriptionStartDate: string | null;
  nextResetDate: string | null;
  billingPeriodDays: number | null;
  resetCounter: number;
  daysRemaining: number;
  periodStart: string | null;
  periodEnd: string | null;
  documentsProcessed: number;
  documentsLimit: number;
  isActive: boolean;
  isExpired: boolean;
  isInGracePeriod: boolean;
  gracePeriodRemaining: number;
  daysOverdue: number;
  usagePercent: number;
  warningLevel: 'none' | 'warning' | 'critical';
  canProcess: boolean;
  cannotProcessReason: string | null;
  pendingDowngrade: {
    newLimit: number;
    newPlanCode: string;
    effectiveDate: string;
  } | null;
  renewalRecommendation: string | null;
  upgradeRecommendation: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  stripePriceId?: string | null;
  stripeSubscriptionStatus?: string | null;
  cancelAtPeriodEnd?: boolean;
  autoRenew?: boolean;
  stripePeriodStart?: string | null;
  stripePeriodEnd?: string | null;
  latestInvoiceId?: string | null;
  latestPaymentStatus?: string | null;
  stripeCheckoutSessionId?: string | null;
  stripeLastEventId?: string | null;
  isPaidStripeSubscription?: boolean;
};

export type StripePurchaseHistoryRow = {
  planName: any;
  eventId: string;
  stripeSessionId: string | null;
  pagesPurchased: number;
  uid: string | null;
  timestamp: string | null;
  createdOn: string | null;
  stateCode: number | null;
  purchaseType: "extra_pages" | "plan_or_subscription";
};

class ApiService {
  private uploadInstance: AxiosInstance;
  private apiInstance: AxiosInstance;
  private tenantId = "";
  private userId = "";
  client: any;

  private purchaseHistoryEndpointMissing = false;

  constructor() {
    this.uploadInstance = axios.create({
      baseURL: API_BASE_URL
    });

    this.apiInstance = axios.create({
      baseURL: `${API_BASE_URL}/v1`
    });

    this.apiInstance.interceptors.request.use(
      async (
        config: InternalAxiosRequestConfig
      ): Promise<InternalAxiosRequestConfig> => {
        return this.attachAuth(config, "api");
      },
      (error) => Promise.reject(error)
    );

    this.apiInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            let user = auth.currentUser;
            if (!user) {
              await waitForAuth();
              user = auth.currentUser;
            }

            if (user) {
              const newToken = await user.getIdToken(true);

              originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
              originalRequest.headers.set("X-User-Id", user.uid);

              if (this.tenantId) {
                originalRequest.headers.set("X-Tenant-Id", this.tenantId);
              }

              return this.apiInstance(originalRequest);
            }
          } catch (refreshError) {
            if (process.env.NODE_ENV === "development") {
              console.error("Token refresh failed:", refreshError);
            }
          }

          window.location.href = "/login";
        }

        return Promise.reject(error);
      }
    );

    this.uploadInstance.interceptors.request.use(
      async (
        config: InternalAxiosRequestConfig
      ): Promise<InternalAxiosRequestConfig> => {
        return this.attachAuth(config, "upload");
      },
      (error) => Promise.reject(error)
    );
  }

  private async attachAuth(
    config: InternalAxiosRequestConfig,
    label: string
  ): Promise<InternalAxiosRequestConfig> {
    try {
      let user = auth.currentUser;

      if (!user) {
        if (process.env.NODE_ENV === "development") {
          console.log(`⏳ [${label}] Waiting for auth...`);
        }
        await waitForAuth();
        user = auth.currentUser;
      }

      const token = await getAuthToken();

      if (token && user) {
        config.headers.set("Authorization", `Bearer ${token}`);
        config.headers.set("X-User-Id", user.uid);

        if (this.tenantId) {
          config.headers.set("X-Tenant-Id", this.tenantId);
        }

        if (process.env.NODE_ENV === "development") {
          console.log(`🔑 [${label}] Auth attached → ${config.url}`);
        }
      } else if (process.env.NODE_ENV === "development") {
        console.warn(`⚠️ [${label}] Missing token or user`);
      }

      return config;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error(`❌ [${label}] attachAuth failed:`, err);
      }
      return config;
    }
  }

  public setTenantContext(tenantId: string, userId: string): void {
    this.tenantId = tenantId;
    this.userId = userId;
  }

  public async createCheckoutSession(
    data: CheckoutRequest
  ): Promise<{ url: string }> {
    const response = await this.apiInstance.post(
      "/billing/create-checkout-session",
      data
    );

    return response.data;
  }

public async getStripeOrder(
  sessionId: string
): Promise<StripeOrderResponse> {
  const response = await this.uploadInstance.post("/stripe/order", {
    sessionId,
  });

  return response.data;
}


  public async previewPlanChange(data: ChangePlanRequest): Promise<PlanChangePreviewResponse> {
    const response = await this.apiInstance.post("/billing/preview-plan-change", data);
    return response.data;
  }

  // Change an existing paid subscription only after the customer confirms the preview.
  public async changePlan(data: ChangePlanRequest): Promise<ChangePlanResponse> {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    try {
      const response = await this.apiInstance.post(
        "/billing/change-plan",
        data
      );
      return response.data;
    } catch (error: any) {
      // Preserve the error response so the frontend can show specific messages
      if (error.response?.data) {
        throw {
          ...error,
          response: error.response
        };
      }
      throw error;
    }
  }

  public async getSubscription(): Promise<SubscriptionResponse> {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const response = await this.apiInstance.get("/billing/subscription");
    return response.data;
  }

  public async updateAutoRenew(autoRenew: boolean): Promise<{
    autoRenew: boolean;
    cancelAtPeriodEnd: boolean;
    stripeSubscriptionStatus: string;
    stripePeriodStart: string | null;
    stripePeriodEnd: string | null;
  }> {
    const response = await this.apiInstance.post("/billing/auto-renew", {
      autoRenew
    });

    return response.data;
  }

  public async getPurchaseHistory(): Promise<StripePurchaseHistoryRow[]> {
    if (this.purchaseHistoryEndpointMissing) {
      return [];
    }

    try {
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      const response = await this.apiInstance.get("/billing/purchase-history", {
        headers: {
          "X-User-Id": user.uid
        }
      });

      return response.data?.items ?? [];
    } catch (error: any) {
      if (error?.response?.status === 404) {
        this.purchaseHistoryEndpointMissing = true;

        if (process.env.NODE_ENV === "development") {
          console.warn(
            "Purchase history endpoint not found. Disabling further history calls for this session."
          );
        }

        return [];
      }

      if (process.env.NODE_ENV === "development") {
        console.error("Error fetching purchase history:", error);
      }

      throw error;
    }
  }

  public isPurchaseHistoryEndpointMissing(): boolean {
    return this.purchaseHistoryEndpointMissing;
  }

  public async deleteTransaction(id: string): Promise<boolean> {
  try {
    // Ensure user is authenticated before making the request
    const user = auth.currentUser;
    if (!user) {
      console.error("No authenticated user");
      return false;
    }
    
    // Get fresh token
    const token = await user.getIdToken(true);
    
    // Make the delete request with explicit headers
    const response = await this.apiInstance.delete(`/transactions/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-User-Id': user.uid
      }
    });
    
    return response.status === 200;
  } catch (error: any) {
    console.error("Error deleting transaction:", error);
    console.error("Status:", error.response?.status);
    console.error("Message:", error.response?.data?.message);
    return false;
  }
}

  public async createTransaction(data: {
  ppp_sf_job: string;
  ppp_description: string;
  ppp_amountsigned: number;
  ppp_transactiondate: string;
  ppp_category?: string;
}): Promise<{ id: string }> {
  const response = await this.apiInstance.post("/transactions", data);
  return response.data;
}

  async bulkUpdateTransactions(updates: any[]) {
    return this.client.patch("/v1/transactions/bulk", {
      updates
    });
  }

  public async getJobs(): Promise<any[]> {
    try {
      const response = await this.apiInstance.get("/jobs");
      return response.data?.jobs ?? response.data ?? [];
    } catch (error) {
      console.error("Error fetching jobs:", error);
      return [];
    }
  }

  public async getTransactions(jobId: string): Promise<any> {
    try {
      const response = await this.apiInstance.get(`/jobs/${jobId}/extract`);
      return response.data;
    } catch (error: any) {
      return { transactions: [], error: error.message };
    }
  }

  public async updateTransaction(transactionId: string, payload: any): Promise<any> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "Not authenticated" };
    }
    
    const token = await user.getIdToken(true);
    
    const response = await this.apiInstance.patch(
      `/transactions/${transactionId}`,
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-Id': user.uid
        }
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Update transaction error:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to update transaction"
    };
  }
}

  public async uploadStatement(file: File, bankType?: string): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("bankType", bankType || "auto");

    try {
      const response = await this.uploadInstance.post(
        "/v1/statements/upload",
        formData
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || "Upload failed"
      };
    }
  }

  public async extractStatement(jobId: string, file?: File): Promise<any> {
    try {
      if (file) {
        const formData = new FormData();
        formData.append("pdf", file);

        const response = await this.apiInstance.post(
          `/jobs/${jobId}/extract`,
          formData
        );

        return response.data;
      } else {
        const response = await this.apiInstance.post(
          `/jobs/${jobId}/extract`,
          {}
        );
        return response.data;
      }
    } catch (error: any) {
      return {
        success: false,
        error:
          error.response?.data?.error || error.message || "Extraction failed"
      };
    }
  }



  
  public async getJobStatus(jobId: string): Promise<any> {
    try {
      const response = await this.apiInstance.get(`/jobs/${jobId}/status`);
      return response.data;
    } catch (error: any) {
      return { status: "failed", error: error.message };
    }
  }

  public async exportToCsv(
jobId: string, includeCategories: boolean = false ): Promise<{ blob: Blob; fileName: string }> {
    const response = await this.apiInstance.get(`/jobs/${jobId}/csv`, {
      responseType: "blob"
    });

    const disposition = response.headers["content-disposition"];
    let fileName = `Statement_${jobId}.csv`;

    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }

    return { blob: response.data, fileName };
  }

  public async deleteJob(jobId: string): Promise<boolean> {
    try {
      await this.apiInstance.delete(`/jobs/${jobId}`);
      return true;
    } catch (error) {
      console.error("Error deleting job:", error);
      return false;
    }
  }
}

const apiService = new ApiService();
export default apiService;